/**
 * Pure derivation of description autocomplete suggestions from the
 * user's own past time entries.
 *
 * Two modes, driven by the `projectId` argument:
 *
 *   - **Project mode** (`projectId` set) — suggestions scoped to that
 *     one project, ordered most-used first. Mirrors the
 *     project→designation gating in the tracker form.
 *   - **Global mode** (`projectId` empty) — the Clockify behaviour:
 *     the user clicks the empty description field and sees their last
 *     tasks across every project, newest first. Picking one back-fills
 *     project + designation + duration.
 *
 * Either way, rows are deduped by a normalized (project, description,
 * designation) key so the same recurring task collapses to a single row
 * regardless of casing/whitespace or which week it ran.
 *
 * Pure module — no React, no Firestore. Unit-tested.
 */

import type { TimeEntry } from '@altyor-kronos/shared-types';
import { foldText } from './entryFilter';

/** Global mode would otherwise dump the user's whole history into the popup. */
const DEFAULT_GLOBAL_LIMIT = 12;

export interface TaskSuggestion {
  /** Display text — the most recently used original casing. */
  label: string;
  /** Project the task was logged on. */
  projectId: string;
  /** Resolved project label ("P26014 — E-BIKE"), or the raw id when unknown. */
  projectLabel: string;
  /** Raw designation doc id (NOT encoded). */
  designationId: string;
  /** Resolved designation label, or the raw id when unknown. */
  designationLabel: string;
  /** Duration of the most recent occurrence, in seconds — pre-fills the manual form. */
  durationSeconds: number;
  /** How many times this (task, designation) pair was logged. */
  count: number;
  /** Most recent startTime in epoch ms — récence tie-breaker. */
  lastUsedAt: number;
}

export interface BuildTaskSuggestionsOptions {
  /**
   * Raw designation doc ids currently valid on the project. Project mode
   * only — when provided, suggestions referencing a designation no longer
   * on the project are dropped (it could not be pre-filled anyway).
   */
  validDesignationIds?: ReadonlySet<string>;
  /**
   * Raw designationId → label, for the dropdown's secondary text. In
   * global mode this map doubles as the validity check: it is built from
   * the designation docs of every project in the history, so an id absent
   * from it no longer exists.
   */
  designationLabelsById?: ReadonlyMap<string, string>;
  /**
   * Raw projectId → label, for the dropdown's secondary text. Also a
   * validity check: it is built from the tracker's project picker, which
   * lists active projects only, so an id absent from it can neither be
   * labelled nor pre-filled into the picker (archived project).
   */
  projectLabelsById?: ReadonlyMap<string, string>;
  /** Global-mode row cap. Defaults to 12. */
  limit?: number;
}

/** Normalize a description for dedup: accent/case-fold + collapse whitespace. */
function normalizeDescription(s: string): string {
  return foldText(s).replace(/\s+/g, ' ').trim();
}

export function buildTaskSuggestions(
  entries: readonly TimeEntry[],
  projectId: string,
  opts: BuildTaskSuggestionsOptions = {},
): TaskSuggestion[] {
  const {
    validDesignationIds,
    designationLabelsById,
    projectLabelsById,
    limit = DEFAULT_GLOBAL_LIMIT,
  } = opts;
  const global = projectId === '';

  const byKey = new Map<string, TaskSuggestion>();

  for (const e of entries) {
    if (!global && e.projectId !== projectId) continue;
    if (projectLabelsById && !projectLabelsById.has(e.projectId)) continue;
    const raw = e.description ?? '';
    const normalized = normalizeDescription(raw);
    if (!normalized) continue;
    if (global) {
      // No per-project designation subscription to check against; the
      // label map covers every project in the history, so absence from
      // it means the designation is gone.
      if (designationLabelsById && !designationLabelsById.has(e.designationId)) continue;
    } else if (validDesignationIds && !validDesignationIds.has(e.designationId)) {
      continue;
    }

    const key = `${e.projectId}|${normalized}|${e.designationId}`;
    const usedAt = e.startTime.toMillis();
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
      // Keep the label, duration + lastUsedAt from the most recent occurrence.
      if (usedAt > existing.lastUsedAt) {
        existing.lastUsedAt = usedAt;
        existing.label = raw.trim();
        existing.durationSeconds = e.durationSeconds;
      }
    } else {
      byKey.set(key, {
        label: raw.trim(),
        projectId: e.projectId,
        projectLabel: projectLabelsById?.get(e.projectId) ?? e.projectId,
        designationId: e.designationId,
        designationLabel:
          designationLabelsById?.get(e.designationId) ?? e.designationId,
        durationSeconds: e.durationSeconds,
        count: 1,
        lastUsedAt: usedAt,
      });
    }
  }

  const rows = [...byKey.values()];
  if (global) {
    // "The last things you worked on" — pure recency, capped.
    return rows.sort((a, b) => b.lastUsedAt - a.lastUsedAt).slice(0, limit);
  }
  return rows.sort((a, b) => b.count - a.count || b.lastUsedAt - a.lastUsedAt);
}
